import { formatDischargeDateTimeIST } from './planDischargeModel';
import { DOD_REASON_MAX_LENGTH } from './dischargeModel';
import { DISCHARGE_TYPE } from '../../../constants/admission';

/**
 * Backdate / "left earlier" reason PICK-LIST (owner 2026-10-13, after the
 * regulatory review — audit-safe wording, exact sentences R1–R5 + Other).
 * The server stores the SENTENCE (`discharge_date_reason`) and the machine
 * CODE (`discharge_date_reason_code`). The sentence is rebuilt from the FINAL
 * discharge date at submit, never kept from an earlier pick.
 * "Other" is typed. Never offer "bill pending" / "insurance awaited".
 */
export const REASON_OTHER = 'OTHER';

export const SIGNER_OPTIONS = [
  { key: 'patient', label: 'Patient' },
  { key: 'attendant', label: 'Attendant' },
];

const when = (iso) => formatDischargeDateTimeIST(iso);

// key === backend reason code.
const OPTIONS = [
  { key: 'LATE_ENTRY', build: (iso) => `Patient was discharged on ${when(iso)}; the discharge was entered in the system today.` },
  { key: 'NIGHT_NEXT_MORNING', build: (iso) => `Patient left at ${when(iso)} (night); the discharge was entered the next morning.` },
  { key: 'LAMA_FORM_SIGNED', needsSigner: true, build: (iso, { signer }) => `Patient left against medical advice at ${when(iso)}; LAMA form signed by ${signer}.` },
  { key: 'ABSCONDED', build: (iso, { staff }) => `Patient left without informing staff (absconded); noticed at ${when(iso)} by ${staff}.` },
  { key: 'TRANSFERRED', build: (iso) => `Patient was transferred to another organisation at ${when(iso)}; the discharge was entered in the system today.` },
  { key: 'SYSTEM_OUTAGE', build: () => 'The system was unavailable at the time of discharge (power/internet outage); entered when restored.' },
];

/**
 * Options offered for this discharge type + days back (>=1):
 * R2 only when the date is yesterday; R3a only LAMA/DAMA; R4 only
 * transfer/referral; R3b any non-death type (needs the user's name).
 */
export function reasonOptionsFor({ type, daysBack, staff = '' }) {
  if (type === DISCHARGE_TYPE.DECEASED) return [];
  return OPTIONS.filter((o) => {
    if (o.key === 'NIGHT_NEXT_MORNING') return daysBack === 1;
    if (o.key === 'LAMA_FORM_SIGNED') return type === DISCHARGE_TYPE.LAMA || type === DISCHARGE_TYPE.DAMA;
    if (o.key === 'TRANSFERRED') return type === DISCHARGE_TYPE.REFERRED;
    if (o.key === 'ABSCONDED') return !!staff; // sentence names the current user
    return true;
  });
}

/**
 * {text, code} for a pick, built from `valueIso` (call at submit with the
 * FINAL date). Empty text when the pick is unusable (unknown key, LAMA signer
 * not chosen, blank Other).
 */
export function resolveDodReason(key, otherText, valueIso, { signer = '', staff = '' } = {}) {
  let text = '';
  if (key === REASON_OTHER) text = String(otherText || '').trim();
  else {
    const opt = OPTIONS.find((o) => o.key === key);
    if (opt && Number.isFinite(Date.parse(valueIso)) && (!opt.needsSigner || signer)) {
      text = opt.build(valueIso, { signer, staff });
    }
  }
  text = text.slice(0, DOD_REASON_MAX_LENGTH);
  return { text, code: text ? key : '' };
}
